/**
 * SafeHtmlContent
 *
 * Renders a small, controlled subset of rich text HTML as React elements.
 *
 * Primary responsibilities:
 * - Preserve common formatting from stored rich text
 * - Drop scripts, event handlers, unsafe links, and unsupported tags
 *
 * Dependencies:
 * - React
 */

'use client'

import React, { useMemo } from 'react'

type SafeHtmlContentProps = {
  className?: string
  html: string
}

const ALLOWED_TAGS = new Set([
  'a',
  'blockquote',
  'br',
  'code',
  'div',
  'em',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'li',
  'ol',
  'p',
  'pre',
  'span',
  'strong',
  'u',
  'ul'
])

const BLOCKED_TAGS = new Set(['iframe', 'object', 'script', 'style', 'svg'])

// --------------

export function SafeHtmlContent({ className, html }: SafeHtmlContentProps) {
  const content = useMemo(() => parseSafeHtml(html), [html])

  return <div className={className}>{content}</div>
}

// --------------

function parseSafeHtml(html: string) {
  if (typeof window === 'undefined') {
    return stripHtmlTags(html)
  }

  const document = new DOMParser().parseFromString(html, 'text/html')
  return Array.from(document.body.childNodes).map((node, index) =>
    renderSafeNode(node, `safe-html-${index}`)
  )
}

// --------------

function renderSafeNode(node: ChildNode, key: string): React.ReactNode {
  if (node.nodeType === Node.TEXT_NODE) {
    return node.textContent
  }

  if (!(node instanceof HTMLElement)) {
    return null
  }

  const tagName = node.tagName.toLowerCase()
  if (BLOCKED_TAGS.has(tagName)) {
    return null
  }

  const children = Array.from(node.childNodes).map((child, index) =>
    renderSafeNode(child, `${key}-${index}`)
  )

  if (!ALLOWED_TAGS.has(tagName)) {
    return children
  }

  if (tagName === 'a') {
    const href = node.getAttribute('href') ?? ''
    return (
      <a key={key} href={getSafeHref(href)} rel="noopener noreferrer" target="_blank">
        {children}
      </a>
    )
  }

  return React.createElement(tagName, { key }, children)
}

// --------------

function getSafeHref(href: string) {
  const trimmed = href.trim()

  if (/^(https?:|mailto:)/i.test(trimmed)) {
    return trimmed
  }

  return '#'
}

// --------------

function stripHtmlTags(value: string) {
  return value.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
}
